/**
 * Provider transport boundary (Phase 9B.1, spec §10 + §26A).
 *
 * The application NEVER holds a provider API key: it asks an executor to
 * perform a provider request and receives JSON back. Today the desktop
 * shell (Rust) is the credential holder; under SaaS the same interface is
 * implemented by a call to a Berrier Reno server endpoint that holds the
 * key server-side. Neither implementation puts a secret in shipped
 * frontend JavaScript, and no domain code changes when the transport moves.
 */

export type ProviderErrorKind =
  | "not-configured"
  | "credentials"
  | "quota"
  | "network"
  | "provider"
  | "configuration";

export class ProviderError extends Error {
  constructor(
    readonly kind: ProviderErrorKind,
    message: string,
  ) {
    super(message);
    this.name = "ProviderError";
  }
}

export interface ProviderCredentialStatus {
  configured: boolean;
  /** Where the desktop build expects its local credentials file. Never a key. */
  path: string;
}

export interface ProviderExecutor {
  /** One provider request. `params` are the engine's own parameters. */
  request(engine: string, params: Record<string, string>): Promise<unknown>;
  /** Whether live sourcing is available in this build/installation. */
  credentialStatus(): Promise<ProviderCredentialStatus>;
}

/** Shape the Rust command returns when it rejects. */
interface RawProviderError {
  kind?: string;
  message?: string;
}

export function toProviderError(err: unknown): ProviderError {
  if (err instanceof ProviderError) return err;
  const raw = (err ?? {}) as RawProviderError;
  const kind = (raw.kind ?? "provider") as ProviderErrorKind;
  const message =
    raw.message ??
    (typeof err === "string" ? err : "Home Depot product search is temporarily unavailable.");
  return new ProviderError(kind, message);
}

/**
 * Browser preview / any build without a credential holder. Fails honestly —
 * it never falls back to presenting manual observations as live results.
 */
export function createUnavailableProviderExecutor(reason: string): ProviderExecutor {
  return {
    async request() {
      throw new ProviderError("not-configured", reason);
    },
    async credentialStatus() {
      return { configured: false, path: "" };
    },
  };
}
